import { atom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { coreBindings, hotkeyBindingsAtom, type Binding } from '@/gestures/hotkeys'

/*
 * Page keyboard shortcuts. While a page is open its keys are the core ones (undo, redo, escape) plus
 * the keys its own controls declare (`usePageShortcut`, or `shortcut` on a WorldButton); the editor's
 * tool keys are off, so no key reaches a tool the page does not have. Holding ⌘ (or Ctrl) shows each
 * control's key at its corner. The host mounts `PageKeys` for the open page.
 */

type Entry = { id: number; key: string; run: () => void }

const shortcutEntriesAtom = atom<readonly Entry[]>([])

/** The keys of a bar of colours or materials, in order: `<ColorBar shortcuts={NUMBER_KEYS}>`. */
export const NUMBER_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

/** True while the hint key is held: controls with a shortcut show their key. */
export const shortcutHintsVisibleAtom = atom(false)

let nextId = 1
/** Holding the hint key this long shows the hints, so a quick ⌘Z does not flash them. */
const HINT_DELAY_MS = 350

/**
 * Run `run` when `key` (a single character, e.g. 'd' or '2') is pressed on this page, while the
 * caller is mounted. Undefined: no shortcut. When two mounted controls claim a key, the later wins.
 */
export function usePageShortcut(key: string | undefined, run: () => void): void {
  const store = useStore()
  const runRef = useRef(run)
  runRef.current = run
  useEffect(() => {
    if (!key) return
    const id = nextId++
    const entry: Entry = { id, key: key.toLowerCase(), run: () => runRef.current() }
    store.set(shortcutEntriesAtom, (list) => [...list, entry])
    return () => store.set(shortcutEntriesAtom, (list) => list.filter((e) => e.id !== id))
  }, [store, key])
}

function pageBindings(entries: readonly Entry[]): Binding[] {
  const latest = new Map<string, Entry>()
  for (const e of entries) latest.set(e.key, e)
  return [
    ...coreBindings(),
    ...[...latest.values()].map((e): Binding => ({ combo: e.key, run: () => e.run() })),
  ]
}

/** The open page's key bindings and the hold-for-hints key (mounted by the page host). */
export function PageKeys() {
  const store = useStore()
  const entries = useAtomValue(shortcutEntriesAtom)
  const setHints = useSetAtom(shortcutHintsVisibleAtom)

  useLayoutEffect(() => { store.set(hotkeyBindingsAtom, pageBindings(entries)) }, [store, entries])
  useLayoutEffect(() => () => store.set(hotkeyBindingsAtom, null), [store])

  useEffect(() => {
    let timer: number | undefined
    const hide = () => {
      window.clearTimeout(timer)
      timer = undefined
      setHints(false)
    }
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control') {
        if (timer === undefined) timer = window.setTimeout(() => setHints(true), HINT_DELAY_MS)
      } else hide() // a chord (⌘Z) is not a hold
    }
    const up = (e: KeyboardEvent) => { if (e.key === 'Meta' || e.key === 'Control') hide() }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', hide)
    return () => {
      hide()
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', hide)
    }
  }, [setHints])
  return null
}

/** A control's key, drawn at its corner while the hint key is held. */
export function ShortcutHint({ shortcut }: { shortcut: string | undefined }) {
  const visible = useAtomValue(shortcutHintsVisibleAtom)
  if (!shortcut || !visible) return null
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        // Inside the button's box (at the circle's shoulder), so no bar or scroller can clip it.
        top: 0,
        right: 0,
        minWidth: 22,
        height: 22,
        padding: '0 5px',
        borderRadius: 11,
        background: '#1d2b36',
        color: '#ffffff',
        font: '700 13px/22px system-ui, -apple-system, sans-serif',
        textAlign: 'center',
        textTransform: 'uppercase',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 2px #ffffff, 0 2px 6px rgba(20,30,40,0.3)',
        pointerEvents: 'none',
      }}
    >
      {shortcut}
    </span>
  )
}
